import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Box, Typography, IconButton, Paper, Tooltip,FormControl,InputLabel,OutlinedInput, Select,
  MenuItem
} from '@mui/material';
import { FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import FieldHelp from '../ui/FieldHelp';
import { COURSE_DIALOG_FIELD_HELP as HELP } from './courseDialogFieldHelp';
import ViewPdfDialog from "./ViewPdfDialog";
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

export default function FormDialog({
  open,
  handleClose,
  data,
  onChange,
  handleFormSubmit,
  loading,
  saveFeatureKey = 'action.course.add.save',
}) {
    const { courseId, courseName, code, vendor, domains, about, courseType} = data;
    const navigate = useNavigate();
    const [newDomain, setNewDomain] = useState({ name: '', percentage: '', subDomain: [] });
    const [newSubDomain, setNewSubDomain] = useState({ name: '', subTopics: '' });
    const [alertMessage, setAlertMessage] = useState('');
    const [activeDomainIndex, setActiveDomainIndex] = useState(null);
    const [domainsToDelete, setDomainsToDelete] = useState([]);
    const [alertOpen, setAlertOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const { guardAction, isLocked, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  
    const handleViewPdfClick = (courseId) => {
      setSelectedCourseId(courseId);
      setViewDialogOpen(true);
    };

    const getBasicAuthHeader = () => {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const encodedCredentials = btoa(`${username}:${password}`);
        return `Basic ${encodedCredentials}`;
      };

    const generateDomainId = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let domainId = '';
        for (let i = 0; i < 10; i++) {
            domainId += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return domainId;
    };

    const handleAddDomain = () => {
        const percentage = parseFloat(newDomain.percentage);
        if (!newDomain.name || isNaN(percentage) || percentage <= 0 || percentage > 100) {
            setAlertMessage("Please enter a valid domain name and percentage (0-100).");
            return;
        }

        const totalPercentage = domains.reduce((sum, domain) => sum + parseFloat(domain.percentage), 0) + percentage;
        if (totalPercentage > 100) {
            setAlertMessage("Total domain percentage should not exceed 100.");
            return;
        }

        const domainId = generateDomainId();
        const updatedDomains = [...domains, { ...newDomain, percentage: percentage.toString(), domainId }];
        setNewDomain({ name: '', percentage: '', subDomain: [] });
        onChange({ target: { value: updatedDomains, id: 'domains' } });
    };

    const handleAddSubdomain = () => {
        if (newSubDomain.name && newSubDomain.subTopics && activeDomainIndex !== null) {
            const updatedDomains = domains.map((domain, index) => {
                if (index === activeDomainIndex) {
                    return {
                        ...domain,
                        subDomain: [...domain.subDomain, newSubDomain]
                    };
                }
                return domain;
            });
            setNewSubDomain({ name: '', subTopics: '' });
            onChange({ target: { value: updatedDomains, id: 'domains' } });
            setActiveDomainIndex(null);
        } else {
            setAlertMessage("Please fill both subdomain fields before adding.");
        }
    };

    const handleRemoveDomain = async (domainIndex) => {
        const domainId = domains[domainIndex].domainId;
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}/api/questions/${domainId}`, {
                method: 'GET',
                    headers: {
                        'Content-Type': "application/json",
                    "Accept": "*/*",
                    "Access-Control-Allow-Origin": "*",
                    'Authorization': getBasicAuthHeader(),
                    },
            });
            const questions = await response.json();
            if (questions.length > 0) {
                setAlertMessage('Cannot delete Domain as questions are associated with it.')
            } else {
                const updatedDomains = domains.filter((_, index) => index !== domainIndex);
                setDomainsToDelete([...domainsToDelete, domainId]);
                onChange({ target: { value: updatedDomains, id: 'domains' } });
            }
        } catch (error) {
            console.error('Error deleting domain:', error);
        }
    };

    useEffect(() => {
        console.log(domainsToDelete);
    }, [domainsToDelete]);

    const handleRemoveSubdomain = (domainIndex, subdomainIndex) => {
        const updatedDomains = domains.map((domain, index) => {
            if (index === domainIndex) {
                return {
                    ...domain,
                    subDomain: domain.subDomain.filter((_, idx) => idx !== subdomainIndex)
                };
            }
            return domain;
        });
        onChange({ target: { value: updatedDomains, id: 'domains' } });
    };

    const handleFormSubmitWithValidation = () => {
        if (courseName && code && vendor && courseType) {
            handleFormSubmit();
        } else {
            setAlertMessage("Please fill all mandatory fields before submitting the form.");
        }
    };

    const handleSubmit = async () => {
        if (!guardAction(saveFeatureKey)) return;
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            if (domainsToDelete.length !== 0) {
                const requestBody = JSON.stringify(domainsToDelete);
                const response = await fetch(apiUrl + '/course/deleteDomains', {
                    method: 'POST',
                        headers: {
                            'Content-Type': "application/json",
                        "Accept": "*/*",
                        "Access-Control-Allow-Origin": "*",
                        'Authorization': getBasicAuthHeader(),
                        },
                    body: requestBody,
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Failed to send deleted domain IDs: ${errorText}`);
                }
                const contentType = response.headers.get('Content-Type');
                let responseData;
                if (contentType && contentType.includes('application/json')) {
                    responseData = await response.json();
                } else {
                    responseData = await response.text();
                }
                console.log('Domains deleted successfully:', responseData);
                setDomainsToDelete([]);
            }
            await handleFormSubmitWithValidation();
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleAccountAlert = () => {
        navigate('/Portal/Instructor/account');
      };

    return (
        <div>
            <Dialog
                open={open}
                onClose={(e, reason) => { if (reason !== 'backdropClick') handleClose(); }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    {data.courseId ? "Edit course" : "Create a course"}
                    <Typography sx={{ mt: 0.5, fontSize: 12.5, fontWeight: 500, color: 'text.secondary', lineHeight: 1.4 }}>
                        Fill in the course details, then add domains and subdomains below.
                    </Typography>
                </DialogTitle>
                <DialogContent>
                <form>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      id="courseName"
                      label="Course name"
                      value={courseName}
                      onChange={onChange}
                      placeholder="e.g. Clinical Pharmacology"
                      fullWidth
                      size="small"
                      required
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                      <TextField
                        id="code"
                        label="Course code"
                        value={code}
                        onChange={onChange}
                        placeholder="PHARM-204"
                        fullWidth
                        size="small"
                        required
                      />
                      <TextField
                        id="vendor"
                        label="Vendor"
                        value={vendor}
                        onChange={onChange}
                        placeholder="MedEd Press"
                        fullWidth
                        size="small"
                        required
                      />
                    </Box>
                    <FormControl fullWidth size="small" required>
                      <InputLabel id="course-type-label">Course type</InputLabel>
                      <Select
                        labelId="course-type-label"
                        id="courseType"
                        value={courseType || ""}
                        label="Course type"
                        onChange={(e) => onChange({ target: { id: "courseType", value: e.target.value } })}
                      >
                        <MenuItem value="K-12">K-12</MenuItem>
                        <MenuItem value="Certification">Certification</MenuItem>
                        <MenuItem value="University">University</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      id="about"
                      label="About course"
                      value={about}
                      onChange={onChange}
                      placeholder="What will learners learn? Add details to improve assessment quality."
                      fullWidth
                      multiline
                      minRows={3}
                      required
                    />
                  </Box>
                </form>

 <Box mt={3}>
 <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'text.primary' }}>Domains &amp; subdomains</Typography>
 <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', mb: 1.5 }}>
   Add the domains this course covers and weight each one. A field grows as you type — tap it to expand and read the full text.
 </Typography>
{domains.length > 0 ? (
    domains.map((domain, domainIndex) => (
        <Paper
            key={domainIndex}
            variant="outlined"
            sx={{
                p: { xs: 1.5, sm: 2 },
                mb: 1.5,
                borderRadius: '14px',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
            }}
        >
            <Box display="flex" alignItems="center" mb={1}>
                {/* Domain Name Field with Tooltip */}
                <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
                    <TextField
                        value={domain.name}
                        onChange={(e) => {
                            const updatedDomains = [...domains];
                            updatedDomains[domainIndex].name = e.target.value;
                            onChange({ target: { value: updatedDomains, id: 'domains' } });
                        }}
                        label="Domain Name"
                        variant="outlined"
                        margin="dense"
                        size="small"
                        fullWidth
                        required
                        multiline
                        minRows={1}
                        maxRows={4}
                    />
                    <FieldHelp text={HELP.domainName} />
                </Box>

                {/* Domain Percentage Field with Tooltip */}
                <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
                    <TextField
                        value={domain.percentage}
                        onChange={(e) => {
                            const updatedDomains = [...domains];
                            const percentage = parseFloat(e.target.value);
                            if (isNaN(percentage) || percentage < 0 || percentage > 100) {
                                setAlertMessage("Percentage must be a number between 0 and 100.");
                                return;
                            }
                            updatedDomains[domainIndex].percentage = e.target.value;
                            const totalPercentage = updatedDomains.reduce(
                                (sum, domain) => sum + parseFloat(domain.percentage || 0),
                                0
                            );
                            if (totalPercentage > 100) {
                                setAlertMessage("Total domain percentage should not exceed 100.");
                                return;
                            }
                            onChange({ target: { value: updatedDomains, id: 'domains' } });
                        }}
                        label="Percentage"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        required
                    />
                    <FieldHelp text={HELP.domainPercentage} />
                </Box>

                <Tooltip title="Remove domain" arrow>
                  <IconButton aria-label="delete" onClick={() => handleRemoveDomain(domainIndex)} size="small" sx={{ color: 'error.main', mt: 1 }}>
                    <FiTrash2 size={16} />
                  </IconButton>
                </Tooltip>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.secondary', mt: 1, mb: 0.5 }}>Subdomains</Typography>
            {domain.subDomain.length > 0 ? (
                domain.subDomain.map((subdomain, subdomainIndex) => (
                    <Box key={subdomainIndex} display="flex" alignItems="center" mb={1}>
                        {/* Subdomain Name Field with Tooltip */}
                        <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
                            <TextField
                                value={subdomain.name}
                                onChange={(e) => {
                                    const updatedDomains = [...domains];
                                    updatedDomains[domainIndex].subDomain[subdomainIndex].name = e.target.value;
                                    onChange({ target: { value: updatedDomains, id: 'domains' } });
                                }}
                                label="Subdomain Name"
                                variant="outlined"
                                margin="dense"
                                size="small"
                                fullWidth
                                required
                                multiline
                                minRows={1}
                                maxRows={4}
                            />
                            <FieldHelp text={HELP.subdomainName} />
                        </Box>

                        {/* Subtopics Field with Tooltip */}
                        <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
                            <TextField
                                value={subdomain.subTopics}
                                onChange={(e) => {
                                    const updatedDomains = [...domains];
                                    updatedDomains[domainIndex].subDomain[subdomainIndex].subTopics = e.target.value;
                                    onChange({ target: { value: updatedDomains, id: 'domains' } });
                                }}
                                label="Sub Topics"
                                variant="outlined"
                                margin="dense"
                                size="small"
                                fullWidth
                                required
                                multiline
                                minRows={1}
                                maxRows={4}
                            />
                            <FieldHelp text={HELP.subTopics} />
                        </Box>

                        <Tooltip title="Remove subdomain" arrow>
                          <IconButton onClick={() => handleRemoveSubdomain(domainIndex, subdomainIndex)} size="small" sx={{ color: 'error.main', mt: 1 }}>
                            <FiTrash2 size={16} />
                          </IconButton>
                        </Tooltip>
                    </Box>
                ))
            ) : (
                <Typography variant="body2">No subdomains added.</Typography>
            )}
            {activeDomainIndex === domainIndex && (
                <Box display="flex" alignItems="center" mt={1}>
                    <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
                        <TextField
                            value={newSubDomain.name}
                            onChange={(e) => setNewSubDomain({ ...newSubDomain, name: e.target.value })}
                            label="Subdomain Name"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            fullWidth
                            required
                            multiline
                            minRows={1}
                            maxRows={4}
                        />
                        <FieldHelp text={HELP.subdomainNameNew} />
                    </Box>

                    <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
                        <TextField
                            value={newSubDomain.subTopics}
                            onChange={(e) => setNewSubDomain({ ...newSubDomain, subTopics: e.target.value })}
                            label="Sub Topics"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            fullWidth
                            required
                            multiline
                            minRows={1}
                            maxRows={4}
                        />
                        <FieldHelp text={HELP.subTopicsNew} />
                    </Box>

                    <Button onClick={handleAddSubdomain} color="primary" variant="contained" sx={{ mt: '8px' }}>
                        Add Subdomain
                    </Button>
                </Box>
            )}
            <Button onClick={() => setActiveDomainIndex(domainIndex)} color="primary" variant="text" size="small" sx={{ mt: 1 }}>
                + Add subdomain
            </Button>
        </Paper>
    ))
) : (
    <Typography variant="body2">No domains added.</Typography>
)}
<Box display="flex" alignItems="center" mt={2}>
    <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
        <TextField
            value={newDomain.name}
            onChange={(e) => setNewDomain({ ...newDomain, name: e.target.value })}
            label="Domain Name"
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            required
            multiline
            minRows={1}
            maxRows={4}
        />
        <FieldHelp text={HELP.domainNameNew} />
    </Box>

    <Box display="flex" alignItems="center" flexGrow={1} sx={{ mr: 1 }}>
        <TextField
            value={newDomain.percentage}
            onChange={(e) => setNewDomain({ ...newDomain, percentage: e.target.value })}
            label="Percentage"
            variant="outlined"
            margin="dense"
            fullWidth
            required
        />
        <FieldHelp text={HELP.domainPercentageNew} />
    </Box>

    <Button onClick={handleAddDomain} color="primary" variant="contained">
        Add Domain
    </Button>
</Box>
<ViewPdfDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        courseId={selectedCourseId}
      />
                    </Box>
                    {alertMessage && (
                <Box
                    position="fixed"
                    top="42%"
                    left="50%"
                    sx={{ transform: 'translate(-50%, -50%)', border: '1px solid oklch(0.915 0.006 260)' }}
                    bgcolor="oklch(1 0 0)"
                    borderRadius="16px"
                    boxShadow="0 24px 50px -16px rgba(17,20,38,.30)"
                    p={4}
                    zIndex={9999}
                    textAlign="center"
                >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 650 }}>{alertMessage}</Typography>
                    <Button onClick={() => setAlertMessage('')} variant="contained">Close</Button>
                </Box>
            )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {loading ? <ButtonSpinnerSlot size={20} /> : (data.courseId ? 'Save' : 'Create course')}
                    </Button>
                </DialogActions>
                <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
        <DialogTitle>Enter Account Details</DialogTitle>
        <DialogContent>
          <p>You must enter account details to add price.</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)} color="primary">
            OK
          </Button>
          <Button onClick={handleAccountAlert} color="primary" autoFocus disabled={loading}>
              {loading ? <ButtonSpinnerSlot size={24} /> : 'Enter Account Details'}
            </Button>
        </DialogActions>
      </Dialog>
            </Dialog>
            <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
        </div>
    );
}